export const USER_FEEDBACK_DATA_TYPES = [
  "NUMERIC",
  "CATEGORICAL",
  "BOOLEAN",
] as const;
export type UserFeedbackDataType = (typeof USER_FEEDBACK_DATA_TYPES)[number];

export const USER_FEEDBACK_SOURCES = ["USER", "EVAL", "SYSTEM"] as const;
export type UserFeedbackSource = (typeof USER_FEEDBACK_SOURCES)[number];

export const NAMI_CHAT_THUMBS = {
  subjectType: "nami_chat_message",
  scoreName: "thumbs",
} as const;

/**
 * Thumbs on an assistant ACTION card (a change it applied, or a preview it
 * produced). `subjectId` is the tool call id — the same key the harness
 * tool-call ledger uses, so outcome analysis can join the two.
 */
export const HARNESS_ACTION_THUMBS = {
  subjectType: "harness_tool_call",
  scoreName: "action_helpful",
} as const;

/**
 * Ship & Debit 849 expert labels — every reviewer answer on the 849 review surface
 * (/ship-and-debit/849-review) and the labeling record is persisted as a label so the email classifier /
 * matcher can be trained and evaluated against expert ground truth. The label row's
 * metadata carries the engine's PREDICTION at action time (detected supplier,
 * confidence signals, surfaced candidates), making each row a ready-made
 * prediction-vs-truth training pair.
 */
export const SD_849_LABELS = {
  /** Is this inbound email a supplier 849 response? (the classifier's core question) */
  disposition: {
    subjectType: "comm_inbound_email",
    scoreName: "sd_849_disposition",
  },
  /** Which S&D supplier this email belongs to (value = company/supplier UUID). */
  supplier: {
    subjectType: "comm_inbound_email",
    scoreName: "sd_849_supplier",
  },
  /** Claim identity: does this email belong to an existing 849 response?
   *  (value = a SD849ClaimIdentityValue; target response id in metadata) */
  claimIdentity: {
    subjectType: "comm_inbound_email",
    scoreName: "sd_849_claim_identity",
  },
  /** Expert pinned an 849 line to a specific 844 detail line (ground truth for the
   *  matcher; line index + sd_e844d id in metadata). */
  lineMatch: {
    subjectType: "sd_849_response",
    scoreName: "sd_849_line_match",
  },
  /**
   * Which 844 submission(s) this 849 answers — the document-level match label.
   *
   * A single 849 legitimately answers SEVERAL 844s (batch responses), but the label
   * store keeps ONE live row per (org, subject, scoreName, user). So this label keys
   * on the compound subject `${responseId}:${submissionId}` with an
   * {@link SD849DocumentMatchValue} — giving N rows per response, plus an explicit
   * NEGATIVE for candidates the expert rejected (the higher-value training signal).
   * Same shape as {@link SD_849_LABELS.lineMatch}'s `${responseId}:${lineIndex}`.
   */
  documentMatch: {
    subjectType: "sd_849_response",
    scoreName: "sd_849_document_match",
  },
  /**
   * "An expert reviewed this 849's matching" — the completion marker for the match
   * queue, keyed on the response id alone.
   *
   * Needed as its OWN label because the queue cannot infer completion from the
   * presence of {@link SD_849_LABELS.documentMatch} / {@link SD_849_LABELS.lineMatch}
   * rows: a response with no surfaced 844 candidates and no lines would write neither,
   * and so could never leave the queue. This row always gets written on Confirm.
   */
  matchReview: {
    subjectType: "sd_849_response",
    scoreName: "sd_849_match_review",
  },
  /**
   * WHICH attachment carries the supplier's decision. Without this, extraction has to
   * guess on multi-attachment emails and parse errors get misattributed to the wrong
   * file. Keys on the compound subject `${inboundEmailId}:${attachmentId}`; the
   * whole-email "the decision is in the body" case uses the `body` sentinel id.
   */
  attachmentRole: {
    subjectType: "comm_inbound_email_attachment",
    scoreName: "sd_849_attachment_role",
  },
  /**
   * "What told you?" — which signal(s) the expert says decided their disposition.
   * Optional and skippable. This is the evidence base rule promotion needs: it stops
   * us promoting a subject regex that merely correlates when the attachment is what
   * actually decided. Value is a sorted, comma-joined {@link SD849SignalValue} list
   * so it stays a single categorical cell.
   */
  signalAttribution: {
    subjectType: "comm_inbound_email",
    scoreName: "sd_849_signal_attribution",
  },
} as const;

export const SD_849_DISPOSITION_VALUES = [
  /** The email IS a supplier 849 response (created / attached with line decisions). */
  "is_849",
  /** The email is NOT an 849 (dismissed; any wrongly-created response deleted). */
  "not_849",
  /** The email is not itself an 849, but is context/thread for an existing one. */
  "context_for_849",
] as const;
export type SD849DispositionValue = (typeof SD_849_DISPOSITION_VALUES)[number];

/**
 * "This email belongs to a claim and settles nothing."
 *
 * Named because two unrelated places key on it and a typo in either is silent: the
 * attach role (`attachRoleForLabel` — a mistyped literal routes a context email down
 * the LINES attach, the exact bug #7182 fixed) and the backfill's scan of the label
 * history, which would simply find nothing.
 */
export const SD_849_CONTEXT_DISPOSITION =
  "context_for_849" satisfies SD849DispositionValue;

/**
 * Whether the labeling page asks the reviewer which supplier an email is from — and
 * so whether a submitted label must carry one and may record one.
 *
 * `is_849` always: the supplier is half of what completes it. `context_for_849` only
 * while the email has no 849 of its own, because then the verdict leads to an attach
 * and the supplier is what authorizes it (`supplierAuthorizedByLabel`). Once the
 * email has its own 849 nothing is attached and nobody is asked, so a supplier sent
 * then would be detection's pre-fill recorded as a human answer.
 *
 * Shared because the page and the server both apply it: when they disagreed, the page
 * asked for a supplier on a context label and neither side recorded it, so no context
 * attach could ever run.
 */
export function sd849LabelAsksSupplier(params: {
  disposition: SD849DispositionValue;
  hasExistingResponse: boolean;
}): boolean {
  if (params.disposition === "is_849") return true;
  return (
    params.disposition === SD_849_CONTEXT_DISPOSITION &&
    !params.hasExistingResponse
  );
}

export const SD_849_CLAIM_IDENTITY_VALUES = [
  /** Expert attached this email onto an existing 849 (positive same-claim label). */
  "attached",
  /** Expert attached as context only (same claim, no line decisions). */
  "attached_context",
  /** Expert created a NEW 849 even though candidates were surfaced (negative label:
   *  the surfaced signals did NOT mean same-claim; candidates in metadata). */
  "new_despite_candidates",
] as const;
export type SD849ClaimIdentityValue =
  (typeof SD_849_CLAIM_IDENTITY_VALUES)[number];

export const SD_849_DOCUMENT_MATCH_VALUES = [
  /** This 849 DOES answer that 844 submission. */
  "related",
  /** Surfaced as a candidate and the expert rejected it — an explicit negative. */
  "not_related",
] as const;
export type SD849DocumentMatchValue =
  (typeof SD_849_DOCUMENT_MATCH_VALUES)[number];

export const SD_849_ATTACHMENT_ROLE_VALUES = [
  /** This attachment carries the supplier's decision — extraction should read it. */
  "payload",
  /** Related context (a PO copy, an invoice) but not the decision itself. */
  "supporting",
  /** Signature images, logos, disclaimers — ignore entirely. */
  "irrelevant",
] as const;
export type SD849AttachmentRoleValue =
  (typeof SD_849_ATTACHMENT_ROLE_VALUES)[number];

/** Sentinel attachment id for "the decision is in the email body, not a file". */
export const SD_849_BODY_ATTACHMENT_ID = "body";

/**
 * Source kind for a role label whose document is gone, or was never a nameable file.
 *
 * Shared because BOTH sides read it: the aggregate produces it and the supplier panel
 * names it. Hardcoding the string in the UI would print `unknown` as if it were a
 * file extension the day either end changed.
 */
export const SD_849_UNKNOWN_ATTACHMENT_KIND = "unknown";

/**
 * Where a labelled value came from — per-field provenance on a label row.
 *
 * `prefilled` and `unanswered` both mean "inherited", kept distinct because they
 * come from different controls: a heuristic guess the reviewer left alone, versus a
 * question they never answered at all. A label that records none of these is simply
 * a caller that reports no provenance (every path outside the labeling form, plus
 * every row written before #6759) — it must never be read as `user`.
 *
 * Each field admits only the subset that can actually happen; see the contract's
 * `SD849DispositionSourceSchema` and friends.
 */
export const SD_849_LABEL_VALUE_SOURCES = [
  "user",
  "prefilled",
  "unanswered",
] as const;
export type SD849LabelValueSource = (typeof SD_849_LABEL_VALUE_SOURCES)[number];

/**
 * WHY a reviewer reached their verdict on an email — the structured half of a
 * review-session verdict (#7644). Recorded in the disposition label's metadata
 * beside an optional free-text note, so the classifier can learn the SHAPE of a
 * supplier's emails rather than only their verdicts.
 *
 * Deliberately not tied to one disposition: "reply to our claim email" is as true
 * of a re-issued decision (`is_849`) as of a chase (`context_for_849`).
 */
export const SD_849_REVIEW_REASON_VALUES = [
  /** A reply in the thread of the claim email we sent. */
  "reply_to_claim_email",
  /** The supplier acknowledges receipt of our 844 and decides nothing yet. */
  "acknowledges_844",
  /** An AS400 arrival / processing notice, not a supplier's answer. */
  "as400_arrival_notice",
  /** The supplier asks us for information before it can decide. */
  "asks_for_information",
  /** A credit note is attached. */
  "credit_note_attached",
  /** A corrected or re-issued decision on a claim already answered. */
  "reissued_decision",
  /** Nothing to do with Ship & Debit. */
  "unrelated",
  /**
   * The supplier credits less than claimed and asks us to bill the balance as a
   * payback (Hoffman: "process the balance … for payback referencing 18480").
   */
  "payback_request",
  /** "Accepted in full" in the body, with no report attached. */
  "full_acceptance_in_body",
  /**
   * Totals stated in the body, the line detail held elsewhere — a portal
   * (Belden's "Completed Claim").
   */
  "decision_in_body_lines_elsewhere",
  /**
   * The EDI channel already decided these lines (Mersen's credit emails). Not
   * noise, and never routed to the claim link: the EDI 849 owns the decision.
   */
  "duplicate_of_edi_849",
  /** A colleague in our organization forwarded the supplier's answer. */
  "forwarded_internally",
  /** Our own 844 report sent back to us with no decision on it. */
  "our_claim_echoed_back",
] as const;
export type SD849ReviewReasonValue =
  (typeof SD_849_REVIEW_REASON_VALUES)[number];

/**
 * The reasons that can explain each verdict, in the order the review page offers
 * them (AIA-38). A reason outside its verdict's list is a combination nothing can
 * learn from ("not an 849 — payback request"), so the review session refuses it.
 */
export const SD_849_REVIEW_REASONS_BY_DISPOSITION: Readonly<
  Record<SD849DispositionValue, readonly SD849ReviewReasonValue[]>
> = {
  is_849: [
    "reply_to_claim_email",
    "credit_note_attached",
    "reissued_decision",
    "payback_request",
    "full_acceptance_in_body",
    "decision_in_body_lines_elsewhere",
    "duplicate_of_edi_849",
    "forwarded_internally",
  ],
  not_849: [
    "as400_arrival_notice",
    "acknowledges_844",
    "asks_for_information",
    "unrelated",
    "duplicate_of_edi_849",
    "our_claim_echoed_back",
    "forwarded_internally",
  ],
  context_for_849: [
    "asks_for_information",
    "acknowledges_844",
    "reply_to_claim_email",
    "credit_note_attached",
    "duplicate_of_edi_849",
    "our_claim_echoed_back",
    "forwarded_internally",
  ],
};

/**
 * Whether a verdict must carry at least one reason (#7644).
 *
 * A "no" or a "context" is the verdict whose shape the classifier cannot learn from
 * the verdict alone — both collapse very different emails (an AS400 notice, a
 * chase, an unrelated newsletter) into one value. An `is_849` is already explained
 * by the claim and parse that follow it, so its reasons stay optional.
 */
export function sd849ReviewReasonsRequired(
  disposition: SD849DispositionValue,
): boolean {
  return disposition !== "is_849";
}

/**
 * Where the engine read the claim a candidate names (#7645) — the provenance chip
 * the review panel shows beside a suggested claim. In the order a reviewer trusts
 * them: our VDM number echoed in the subject, then the report's own rows, then the
 * conversation's other 849s, then lines this email already decided.
 */
export const SD_849_CLAIM_SIGNAL_VALUES = [
  "subject",
  "report_rows",
  "thread",
  "matched_lines",
] as const;
export type SD849ClaimSignalValue = (typeof SD_849_CLAIM_SIGNAL_VALUES)[number];

/**
 * Why a report row confirmed through the claim panel was NOT placed on a line
 * (#7645). An unplaced row keeps its line pending and moves no money; the reason
 * says what a person would have to settle to place it.
 */
export const SD_849_UNPLACED_REASON_VALUES = [
  /** The row names a VDM that none of the linked claims carries. */
  "no_vdm",
  /** The row's key fits lines on more than one of the linked claims. */
  "ambiguous_claim",
  /** No line on the linked claims has the row's key. */
  "no_candidate_line",
  /** Several lines on one claim fit the row, or another row took the line. */
  "several_candidate_lines",
  /** The row's claimed amount is not what we claimed for the line. */
  "claimed_amount_differs",
  /**
   * Every line that fits is already decided, and this email does not supersede
   * that decision: it came from EDI or another path with no dated source, from
   * this same email or a later one, from a person after this email arrived, or
   * it already reads what this row says (#7651).
   */
  "line_already_settled",
  /** The claimed or credited amount cannot be read, or is out of range. */
  "unreadable_amount",
  /**
   * One row answers several of our lines and is neither paid in full nor paid
   * nothing (SD-87): any split would guess per-line outcomes, so it is left
   * for review as the supplier sent it.
   */
  "lump_row_not_paid_as_claimed",
] as const;
export type SD849UnplacedReasonValue =
  (typeof SD_849_UNPLACED_REASON_VALUES)[number];

/**
 * Why a claim's record lists a row the OLD matcher (`match_849_email`) left
 * unmatched (SD-4). Derived when the record is read and never stored on the
 * row, so the row stays out of the unplaced counts and labels; a re-point of
 * such a row records it in its activity's metadata.
 */
export const SD_849_OLD_MATCHER_UNMATCHED = "old_matcher_unmatched";

/** Every reason a claim's record gives for a row that has no line. */
export const SD_849_RECORD_UNPLACED_REASON_VALUES = [
  ...SD_849_UNPLACED_REASON_VALUES,
  SD_849_OLD_MATCHER_UNMATCHED,
] as const;
export type SD849RecordUnplacedReasonValue =
  (typeof SD_849_RECORD_UNPLACED_REASON_VALUES)[number];

/**
 * The supplier's decision as a reviewer states it (#7651), for an email the
 * parser cannot read line by line — a body-only reply, or figures in a PDF:
 *
 *  - `accepted_in_full` — every still-open line of the linked claims is credited
 *    what we claimed;
 *  - `rejected_in_full` — every still-open line is credited nothing;
 *  - `partial` — the reviewer states the credited amount per line (read off the
 *    supplier's PDF, or for a row the parser could not read). Lines not named stay
 *    pending: nothing is inferred for them.
 *
 * Every stated credit goes through the one shared disposition rule, so it books
 * exactly what the supplier credited — never topped up to our claim.
 */
export const SD_849_STATED_INTENT_VALUES = [
  "accepted_in_full",
  "rejected_in_full",
  "partial",
] as const;
export type SD849StatedIntentValue =
  (typeof SD_849_STATED_INTENT_VALUES)[number];

/**
 * The report columns the parse view shows and names (#7647), in the order a
 * reviewer reads a row: which invoice, which product, which order line, what we
 * claimed, what the supplier credited, and which VDM the row answers.
 */
export const SD_849_PARSE_VIEW_FIELDS = [
  "invoiceNumber",
  "supplierItemCode",
  "salesOrderLineNumber",
  "claimReceivedAmount",
  "supplierCreditAmount",
  "debitMemoNumber",
] as const;
export type SD849ParseViewField = (typeof SD_849_PARSE_VIEW_FIELDS)[number];

/**
 * The canonical fields a parse fix may re-point (#7649): the parse view's six,
 * plus the columns a report states a decision in. Nothing outside the report's
 * columns — sender detection, classification, the reconcile shape, the match
 * key or any tolerance — is reachable from a fix.
 */
export const SD_849_PARSE_FIX_FIELDS = [
  ...SD_849_PARSE_VIEW_FIELDS,
  "owedAmount",
  "batchControlNumber",
  "creditMemoNumber",
  "acceptReject",
  "rejectionCode",
  "rejectionDescription",
  "rejectionComment",
] as const;
export type SD849ParseFixField = (typeof SD_849_PARSE_FIX_FIELDS)[number];

/**
 * Where a supplier writes our VDM numbers (#7650) — each is one REFERENCE
 * CONFIGURATION, ranked per supplier under the same model as the parsing
 * configurations and tried in rank order:
 *
 *  - `subject_token` — "ADM35067", "VDM No 13694" in the subject;
 *  - `prose_debit_memo` — "Vendor Debit Memo No 13625 & 13627" in the subject;
 *  - `reference_cluster` — "16105_CO30218-CO30263", a VDM in a batch-code run;
 *  - `bare_debit_memo` — "DM 18314", our claim without its A or V;
 *  - `batch_range` — "CT30414", "CE30399-CE30411", "+30147 TO +30223": our 844
 *    batch codes (division + batch number), which name claims without a VDM;
 *  - `row_value` — the report column the configuration maps as the debit memo
 *    number, whose whole cell is a VDM.
 *
 * `subject_token` and `row_value` are every supplier's defaults; the others are
 * held only by the suppliers that were migrated to them or were taught them by a
 * reviewer's claim link.
 */
export const SD_849_REFERENCE_STRATEGIES = [
  "subject_token",
  "prose_debit_memo",
  "reference_cluster",
  "bare_debit_memo",
  "batch_range",
  "row_value",
] as const;
export type SD849ReferenceStrategy =
  (typeof SD_849_REFERENCE_STRATEGIES)[number];

/**
 * How a supplier came to hold a reference configuration: every supplier holds
 * the defaults, `migrated` came from the old per-supplier flags, `correction`
 * was proposed by a reviewer's claim link on an email nothing read.
 */
export const SD_849_REFERENCE_ORIGINS = [
  "default",
  "migrated",
  "correction",
] as const;
export type SD849ReferenceOrigin = (typeof SD_849_REFERENCE_ORIGINS)[number];

/** Longest plain-text parse fix a reviewer may describe. */
export const SD_849_PARSE_FIX_INSTRUCTION_MAX_LENGTH = 2000;

/**
 * The configuration the engine reads a report with, among a supplier's
 * configurations in RANK order (#7647, #7648), over row counts already read:
 * the first that reads any row; when none reads a row, the first that has
 * sheets. Before any claim is named this is all the engine can judge — the
 * claim link then tries the configurations in the same order on the linked
 * claims and stops at the first that places its rows. Shared so the parse view
 * names the configuration the engine starts from.
 */
export function sd849ChooseConfiguration(
  configurations: ReadonlyArray<{
    configId: string;
    rowCount: number;
    hasSheets: boolean;
  }>,
): string | null {
  const reading = configurations.find((c) => c.hasSheets && c.rowCount > 0);
  return (
    reading?.configId ??
    configurations.find((c) => c.hasSheets)?.configId ??
    configurations[0]?.configId ??
    null
  );
}

/**
 * Which of an email's attachments may be read as the supplier's REPORT, given the
 * roles a reviewer stated (#7647). The one rule the claim link's parse and the
 * parse view share, so what the reviewer sees parsed is what gets placed.
 *
 *  - A part stated `supporting` or `irrelevant` is never read as the report.
 *  - When any attachment is stated `payload`, only those are read.
 *  - When the email BODY is stated `payload` (`bodyIsPayload`), the body is the
 *    report and no unmarked attachment is read — the files of a forwarded email
 *    included (owner decision, #7647).
 *  - Otherwise, attachments with no stated role (the files of an email forwarded
 *    onto this one, which the form never lists) may be read.
 *  - With no roles stated at all (a caller that asks nothing), every attachment
 *    may be read, as before.
 *  - `sameContent` names, per attachment kept, the byte-identical copies that
 *    deduplication dropped. A role stated on any copy speaks for the kept one,
 *    and `payload` on any copy wins — so marking the second copy as the payload
 *    reads the workbook instead of reading nothing.
 */
export function sd849ReportAttachmentIds(params: {
  attachmentIds: readonly string[];
  statedRoles: ReadonlyArray<{
    attachmentId: string;
    role: SD849AttachmentRoleValue;
  }>;
  sameContent?: ReadonlyArray<{
    attachmentId: string;
    duplicateAttachmentIds?: readonly string[];
  }>;
  /** The reviewer stated the email body is the payload. */
  bodyIsPayload?: boolean;
}): string[] {
  const stated = new Map(
    params.statedRoles.map((r) => [r.attachmentId, r.role]),
  );
  for (const group of params.sameContent ?? []) {
    const roles = [group.attachmentId, ...(group.duplicateAttachmentIds ?? [])]
      .map((id) => stated.get(id))
      .filter((role) => role !== undefined);
    const folded = roles.includes("payload") ? "payload" : roles[0];
    if (folded !== undefined) stated.set(group.attachmentId, folded);
  }
  const anyPayload =
    params.bodyIsPayload === true ||
    [...stated.values()].some((role) => role === "payload");
  return params.attachmentIds.filter((id) => {
    const role = stated.get(id);
    if (role !== undefined) return role === "payload";
    return !anyPayload;
  });
}

export const SD_849_SIGNAL_VALUES = [
  "sender",
  "subject",
  "body",
  "attachment",
] as const;
export type SD849SignalValue = (typeof SD_849_SIGNAL_VALUES)[number];

/** Whether a line's stored match survived expert review — label 4's two outcomes. */
export const SD_849_LINE_MATCH_OUTCOMES = ["agreed", "corrected"] as const;
export type SD849LineMatchOutcome = (typeof SD_849_LINE_MATCH_OUTCOMES)[number];

/**
 * Demand signal for features a user cannot reach yet — the "alert me when this
 * is ready" CTA on the feature-preview pages. `subjectId` is the feature key
 * (see FEATURE_INTEREST_KEYS), so one row per user per feature tells us who
 * asked for what, and the optional comment carries what they'd use it for.
 */
export const FEATURE_INTEREST = {
  subjectType: "feature_interest",
  scoreName: "early_access",
} as const;

/** Features with a preview page. Used as the feedback row's `subjectId`. */
export const FEATURE_INTEREST_KEYS = ["agents", "tasks", "workflows"] as const;
export type FeatureInterestKey = (typeof FEATURE_INTEREST_KEYS)[number];

/**
 * Subject types the label store accepts — one entry per DISTINCT subject type used
 * by the label groups declared above (several groups share a type, e.g. disposition,
 * supplier and signalAttribution all label a `comm_inbound_email`).
 *
 * Ladder rung G1: adding a label group means adding its subject type here, and
 * nothing else — this list is the single registration point. Kept an explicit tuple
 * (rather than derived from `Object.values`) because `z.enum` needs a literal tuple
 * and deriving one would require a cast that lies about its shape.
 */
export const USER_FEEDBACK_SUBJECT_TYPES = [
  NAMI_CHAT_THUMBS.subjectType,
  HARNESS_ACTION_THUMBS.subjectType,
  SD_849_LABELS.disposition.subjectType,
  SD_849_LABELS.lineMatch.subjectType,
  SD_849_LABELS.attachmentRole.subjectType,
  FEATURE_INTEREST.subjectType,
] as const;
export type UserFeedbackSubjectType =
  (typeof USER_FEEDBACK_SUBJECT_TYPES)[number];

export interface UserFeedbackConfigDef {
  subjectType: UserFeedbackSubjectType;
  scoreName: string;
  label: string;
  description: string | null;
  dataType: UserFeedbackDataType;
  numericMin: number | null;
  numericMax: number | null;
  numericStep: number | null;
  categoricalOptions: Array<{ value: string; label: string }> | null;
  commentEnabled: boolean;
  commentRequired: boolean;
  sortOrder: number;
}

export const USER_FEEDBACK_CONFIGS: readonly UserFeedbackConfigDef[] = [
  {
    subjectType: NAMI_CHAT_THUMBS.subjectType,
    scoreName: NAMI_CHAT_THUMBS.scoreName,
    label: "Was the AI assistant's response helpful?",
    description: null,
    dataType: "BOOLEAN",
    numericMin: null,
    numericMax: null,
    numericStep: null,
    categoricalOptions: null,
    commentEnabled: true,
    commentRequired: false,
    sortOrder: 0,
  },
  {
    subjectType: HARNESS_ACTION_THUMBS.subjectType,
    scoreName: HARNESS_ACTION_THUMBS.scoreName,
    label: "Was this assistant action helpful?",
    description:
      "Thumbs on an action the AI assistant applied or previewed (subjectId = tool call id).",
    dataType: "BOOLEAN",
    numericMin: null,
    numericMax: null,
    numericStep: null,
    categoricalOptions: null,
    commentEnabled: true,
    commentRequired: false,
    sortOrder: 0,
  },
  {
    subjectType: SD_849_LABELS.disposition.subjectType,
    scoreName: SD_849_LABELS.disposition.scoreName,
    label: "Is this email a supplier 849 response?",
    description:
      "Expert disposition of an inbound S&D email — ground truth for the 849 classifier.",
    dataType: "CATEGORICAL",
    numericMin: null,
    numericMax: null,
    numericStep: null,
    categoricalOptions: [
      { value: "is_849", label: "Is an 849" },
      { value: "not_849", label: "Not an 849" },
      { value: "context_for_849", label: "Context for an 849" },
    ],
    commentEnabled: true,
    commentRequired: false,
    sortOrder: 0,
  },
  {
    subjectType: SD_849_LABELS.supplier.subjectType,
    scoreName: SD_849_LABELS.supplier.scoreName,
    label: "Which supplier sent this 849?",
    description:
      "Expert-confirmed supplier for an inbound S&D email (value = supplier UUID).",
    dataType: "CATEGORICAL",
    numericMin: null,
    numericMax: null,
    numericStep: null,
    // Options are the org's S&D suppliers — dynamic, so not enumerable here.
    categoricalOptions: null,
    commentEnabled: false,
    commentRequired: false,
    sortOrder: 1,
  },
  {
    subjectType: SD_849_LABELS.claimIdentity.subjectType,
    scoreName: SD_849_LABELS.claimIdentity.scoreName,
    label: "Does this email belong to an existing 849?",
    description:
      "Expert attach-vs-create decision — trains claim identity (which emails are one claim).",
    dataType: "CATEGORICAL",
    numericMin: null,
    numericMax: null,
    numericStep: null,
    categoricalOptions: [
      { value: "attached", label: "Attached to existing" },
      { value: "attached_context", label: "Attached as context" },
      { value: "new_despite_candidates", label: "New despite candidates" },
    ],
    commentEnabled: false,
    commentRequired: false,
    sortOrder: 2,
  },
  {
    subjectType: SD_849_LABELS.lineMatch.subjectType,
    scoreName: SD_849_LABELS.lineMatch.scoreName,
    label: "Line pinned to an 844 detail line",
    description:
      "Expert pinned an 849 response line to a specific sd_e844d line — matcher ground truth.",
    dataType: "CATEGORICAL",
    numericMin: null,
    numericMax: null,
    numericStep: null,
    categoricalOptions: null,
    commentEnabled: false,
    commentRequired: false,
    sortOrder: 3,
  },
  {
    subjectType: FEATURE_INTEREST.subjectType,
    scoreName: FEATURE_INTEREST.scoreName,
    label: "Notify me when this feature is ready",
    description:
      "A user asked for early access to a previewed feature (subjectId = feature key).",
    dataType: "BOOLEAN",
    numericMin: null,
    numericMax: null,
    numericStep: null,
    categoricalOptions: null,
    commentEnabled: true,
    commentRequired: false,
    sortOrder: 0,
  },
];

export function findFeedbackConfig(
  subjectType: UserFeedbackSubjectType,
  scoreName: string,
): UserFeedbackConfigDef | undefined {
  return USER_FEEDBACK_CONFIGS.find(
    (c) => c.subjectType === subjectType && c.scoreName === scoreName,
  );
}

export function listFeedbackConfigs(
  subjectType: UserFeedbackSubjectType,
): UserFeedbackConfigDef[] {
  return USER_FEEDBACK_CONFIGS.filter(
    (c) => c.subjectType === subjectType,
  ).sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Age buckets of the 849 review surface's claim aging view (#7655): days since
 * the claim was sent, inclusive at the top (a claim is past 60 days when
 * `sent_at < asOf - 60 days`). `not_sent` holds claims built but never sent.
 */
export const SD_849_CLAIM_AGE_BUCKETS = [
  "0-30",
  "31-60",
  "61-90",
  "over_90",
  "not_sent",
] as const;
export type Sd849ClaimAgeBucket = (typeof SD_849_CLAIM_AGE_BUCKETS)[number];
